import React from 'react';
import GeoXpWeb from '@geoxp/web';
import {
  EMPTY_GEOXP_CONFIG,
  configHasGeoSpots,
  configHasManualSpots,
  getMapSpotsFromConfig,
  addToPrevious,
  removeFromPrevious,
} from '../utils/geoXp';
import { removeManualCodeLibrarySuffix } from '../utils/manualCode';
import { useOnInit } from '../hooks/useOnInit';
import GeoXpVideoEvents from '../geoxp-video';
import { useLanguage } from './language';

const ExperienceContext = React.createContext(null);

/* init GeoXp instance singleton */
const geoXpInstance = new GeoXpWeb(EMPTY_GEOXP_CONFIG);
window.geoXp = geoXpInstance;

const geoXpVideoEvents = new GeoXpVideoEvents(geoXpInstance.core, EMPTY_GEOXP_CONFIG);

const ExperienceProvider = ({ children }) => {
  const init = useOnInit();
  const { content } = useLanguage();

  /* STATES */
  const [config, setConfig] = React.useState();
  const [mapSpots, setMapSpots] = React.useState([]);
  const [active, setActive] = React.useState([]);
  const [fetching, setFetching] = React.useState([]);
  const [visited, setVisited] = React.useState([]);
  const [location, setLocation] = React.useState(null);
  const [volume, setVolume] = React.useState(1);
  const [mute, setMute] = React.useState(false);
  const [started, setStarted] = React.useState(false);

  const [videoToBeActivated, setVideoToBeActivated] = React.useState([]);
  const [videoActive, setVideoActive] = React.useState([]);

  /* EFFECT - set geoXp listeners */
  React.useEffect(() => {
    if (!init || !geoXpInstance) {
      return;
    }

    // geoXp events subscription
    geoXpInstance.on('active', (spot) => {
      console.info('[GEOXP EVENT] - Spot active', spot.id, spot.label);
      // check if audio spot (videos are managed differently)
      const audio = geoXpInstance.audio?.config?.sounds?.find((e) => e.spotId === spot.id);
      // if so, add to "fetching" array in order to show loader
      if (audio) {
        setFetching((prev) => addToPrevious(prev, spot));
      }
    });

    geoXpInstance.on('inactive', (spot) => {
      console.info('[GEOXP EVENT] - Spot inactive', spot.id, spot.label);
      setActive((prev) => removeFromPrevious(prev, spot.id));
      setFetching((prev) => removeFromPrevious(prev, spot.id));
    });

    geoXpInstance.on('visited', (spot) => {
      console.info('[GEOXP EVENT] - Spot visited', spot.id, spot.label);
      setVisited((prev) => addToPrevious(prev, spot));
    });

    geoXpInstance.on('ready', (sound) => {
      console.info('[GEOXP EVENT] - audio ready to play', sound, sound.cfg.id, sound.cfg.label);

      // if autoplay is disabled in this experience, open the player
      if (!sound.autoplaySounds) {
        const spot = geoXpInstance.core.getSpot(sound.cfg.spotId);
        setActive((prev) => addToPrevious(prev, { ...spot, audio: sound.audio }));

        // autoplay manual spots only
        if (spot.code) sound.audio.play();
      }
    });

    geoXpInstance.on('playing', (sound) => {
      console.info('[GEOXP EVENT] - playing audio', sound, sound.cfg.id, sound.cfg.label);
      const spot = geoXpInstance.core.getSpot(sound.cfg.spotId);
      setActive((prev) => addToPrevious(prev, { ...spot, audio: sound.audio }));
    });

    geoXpInstance.on('stopped', (sound) => {
      console.info('[GEOXP EVENT] - stopped audio', sound, sound.cfg.id, sound.cfg.label);
      setActive((prev) => removeFromPrevious(prev, sound.cfg.spotId));
      setFetching((prev) => removeFromPrevious(prev, sound.cfg.spotId));
    });

    geoXpInstance.on('ended', (sound) => {
      console.info('[GEOXP EVENT] - ended audio', sound, sound.cfg.id, sound.cfg.label);
      setActive((prev) => removeFromPrevious(prev, sound.cfg.spotId));
      setFetching((prev) => removeFromPrevious(prev, sound.cfg.spotId));
    });

    geoXpInstance.on('location', (locationUpdate) => {
      console.info('[GEOXP EVENT] - new location', locationUpdate);
      setLocation(locationUpdate);
    });

    return () => {
      if (geoXpInstance) {
        geoXpInstance.audio.unload();
        geoXpInstance.geolocation.unload();
      }
    };
  }, [geoXpInstance]);

  /* EFFECT - set geoXp video events listeners */
  React.useEffect(() => {
    if (!init || !geoXpVideoEvents || !geoXpInstance) {
      return;
    }

    const handleVideoActive = (video) => {
      console.info('[GEOXP VIDEO EVENT] - Video spot active', video);
      const spot = geoXpInstance.core.getSpot(video.spotId);
      setVideoToBeActivated((prev) => addToPrevious(prev, { ...spot, ...video }));
    };

    const handleVideoInactive = (video) => {
      console.info('[GEOXP VIDEO EVENT] - Video spot inactive', video);
      // remove from active and from "to be activated"
      setVideoActive((prev) => removeFromPrevious(prev, video.id));
      setVideoToBeActivated((prev) => removeFromPrevious(prev, video.id));
    };

    const handleVideoReady = (video) => {
      console.info('[GEOXP VIDEO EVENT] - Video spot ready', video);
      const spot = geoXpInstance.core.getSpot(video.spotId);
      setVideoActive((prev) => addToPrevious(prev, { ...spot, ...video }));
    };

    const handleVideoPlaying = (video) => {
      console.info('[GEOXP VIDEO EVENT] - Video spot playing', video);
      const spot = geoXpInstance.core.getSpot(video.spotId);
      setVideoActive((prev) => addToPrevious(prev, { ...spot, ...video }));
    };

    const handleVideoEnded = (video) => {
      console.info('[GEOXP VIDEO EVENT] - Video spot ended', video);
      // remove from active and from "to be activated"
      setVideoActive((prev) => removeFromPrevious(prev, video.id));
      setVideoToBeActivated((prev) => removeFromPrevious(prev, video.id));
    };

    // geoXp video events subscription
    geoXpVideoEvents.on('video-active', handleVideoActive);
    geoXpVideoEvents.on('video-inactive', handleVideoInactive);
    geoXpVideoEvents.on('video-ready', handleVideoReady);
    geoXpVideoEvents.on('video-playing', handleVideoPlaying);
    geoXpVideoEvents.on('video-ended', handleVideoEnded);

    return () => {
      if (geoXpVideoEvents) {
        geoXpVideoEvents.off('video-active', handleVideoActive);
        geoXpVideoEvents.off('video-inactive', handleVideoInactive);
        geoXpVideoEvents.off('video-ready', handleVideoReady);
        geoXpVideoEvents.off('video-playing', handleVideoPlaying);
        geoXpVideoEvents.off('video-ended', handleVideoEnded);
      }
    };
  }, [geoXpVideoEvents, geoXpInstance]);

  /* EFFECT - set global volume */
  React.useEffect(() => {
    if (!geoXpInstance) return;
    geoXpInstance.audio.setVolume(volume);
  }, [volume, geoXpInstance]);

  /* CALLBACK - update position array (for quick access)  */
  const updatePositions = React.useCallback((cfg) => {
    if (!cfg) return;
    setMapSpots(getMapSpotsFromConfig(cfg));
  }, []);

  /* CALLBACK - load config */
  const loadConfig = React.useCallback(
    (data) => {
      if (!data) return;

      setConfig(data);
      updatePositions(data);
    },
    [updatePositions]
  );

  const initConfig = React.useCallback(() => {
    if (!geoXpInstance || !geoXpVideoEvents || !config) return;
    // reload geoxp config
    geoXpInstance.reload(config);
    geoXpVideoEvents.reload(config.video);
  }, [geoXpInstance, geoXpVideoEvents, config]);

  const hasManualSpots = React.useMemo(() => configHasManualSpots(config), [config]);

  const hasGeoSpots = React.useMemo(() => configHasGeoSpots(config), [config]);

  const isActive = React.useMemo(() => active && active.length > 0, [active]);

  const canReplay = React.useMemo(() => visited && visited.length > 0, [visited]);

  const isVideoActive = React.useMemo(() => videoActive && videoActive.length > 0, [videoActive]);

  // runtime
  const destroy = React.useCallback(() => {
    if (geoXpInstance) {
      geoXpInstance.audio.unload();
      geoXpInstance.geolocation.unload();
    }
    console.info('GeoXp stopped');
  }, [geoXpInstance]);

  const unlock = React.useCallback(() => {
    if (!geoXpInstance) return;

    geoXpInstance.audio.unlock();
    geoXpInstance.geolocation.unlock();
    console.info('GeoXp unlock');
  }, [geoXpInstance]);

  const start = React.useCallback(() => {
    if (!geoXpInstance || !config) return;
    initConfig();
    unlock();
    setStarted(true);
    console.info('GeoXp start');
  }, [geoXpInstance, config, initConfig, unlock]);

  const playManually = React.useCallback(
    (code) => {
      if (!geoXpInstance) {
        return 'geoXpInstance not found';
      }

      if (!config) {
        return 'config not loaded';
      }

      /**
       * check with regex for spots that have ${code}§lib_<uuid>
       * (if it agami global experience them it will have it)
       * then, if multiple correspondance are found, notify user
       */
      const toPlayIds = config.core.patterns.flatMap((pattern) =>
        pattern.spots
          .filter((spot) => removeManualCodeLibrarySuffix(spot.code) === code)
          .map((spot) => spot.id)
      );

      if (toPlayIds.length === 0) {
        console.warn('agami not found');
        return content('agami_not_found');
      }

      if (toPlayIds.length > 1) {
        console.warn('multiple agami found');
        return content('multiple_agami_found');
      }

      return geoXpInstance.core.forceSpot(toPlayIds[0]);
    },
    [config, geoXpInstance, content]
  );

  const stopAll = React.useCallback(() => {
    if (!geoXpInstance) return;

    geoXpInstance.audio.stopAll();
    console.info('GeoXp stop all');
  }, [geoXpInstance]);

  const replay = React.useCallback(() => {
    if (!geoXpInstance) return;

    geoXpInstance.core.replaySpot();
    console.info('GeoXp replay');
  }, [geoXpInstance]);

  const resetStorage = React.useCallback(() => {
    if (!geoXpInstance?.storage) return;
    geoXpInstance.storage.clearAll();
    console.info('GeoXp storage reset');
  }, [geoXpInstance]);

  const fetchLocation = React.useCallback(
    (successCallback, errorCallback) => {
      if (!geoXpInstance) return;
      geoXpInstance.geolocation.fetchCurrentLocation(successCallback, errorCallback);
    },
    [geoXpInstance]
  );

  const simulatePosition = React.useCallback(
    (index) => {
      if (!config) return;
      if (!geoXpInstance) return;

      const concatSpots = config.core.patterns
        .reduce((acc, curr) => [...acc, ...curr.spots], [])
        .filter((sp) => sp !== undefined && sp !== null);

      const spot = concatSpots?.[index];

      if (!spot) {
        console.warn('spot not found', index);
        return 'spot not found';
      }

      const { position } = spot;
      if (!position) {
        console.warn('spot without position', index);
        return 'spot without position';
      }

      geoXpInstance.core.geolocationUpdate({
        ...spot.position,
        timestamp: new Date(),
        accuracy: 10,
      });
    },
    [config, geoXpInstance]
  );

  window.simulatePosition = simulatePosition;

  const simulatePositionCoords = React.useCallback(
    (lon, lat) => {
      if (!geoXpInstance) return;

      console.info('simulating position coords', lon, lat);

      geoXpInstance.core.geolocationUpdate({
        lon,
        lat,
        timestamp: new Date(),
        accuracy: 10,
      });
    },
    [config, geoXpInstance]
  );

  window.simulatePositionCoords = simulatePositionCoords;

  const toggleLocationUpdates = React.useCallback(
    (enabled) => {
      if (!geoXpInstance) return;

      geoXpInstance.geolocation.toggleUpdates(enabled);
    },
    [geoXpInstance]
  );

  window.toggleLocationUpdates = toggleLocationUpdates;

  const contextValue = React.useMemo(
    () => ({
      config,
      hasGeoSpots,
      hasManualSpots,
      mapSpots,
      active,
      fetching,
      isActive,
      location,
      volume,
      mute,
      started,
      canReplay,
      visited,
      loadConfig,
      setVolume,
      setMute,
      unlock,
      destroy,
      start,
      playManually,
      stopAll,
      replay,
      setVisited,
      fetchLocation,
      resetStorage,
      geoXpVideoEvents,
      videoActive,
      isVideoActive,
      videoToBeActivated,
    }),
    [
      config,
      hasGeoSpots,
      hasManualSpots,
      mapSpots,
      active,
      fetching,
      isActive,
      location,
      volume,
      mute,
      started,
      canReplay,
      visited,
      loadConfig,
      unlock,
      destroy,
      start,
      playManually,
      stopAll,
      replay,
      fetchLocation,
      resetStorage,
      geoXpVideoEvents,
      videoActive,
      isVideoActive,
      videoToBeActivated,
    ]
  );

  return <ExperienceContext.Provider value={contextValue}>{children}</ExperienceContext.Provider>;
};

export const useExperience = () => {
  const context = React.useContext(ExperienceContext);
  if (!context) {
    throw new Error('useExperience must be called under a ExperienceContext');
  }
  return context;
};

export default ExperienceProvider;
